/**
 * Regenerable Text Field Component for Puck Editor
 *
 * A custom Puck field that wraps text/textarea fields with AI regeneration capability.
 * Shows a "regenerate" button next to the field with the namespace key.
 *
 * Uses inline styles with CSS variable references so it inherits the host app's
 * dark mode theme without needing its own Tailwind build or <style> tag.
 */

import React, { useState } from "react";
import { Sparkles, Loader2, ChevronDown, AlertCircle, Search } from "lucide-react";
import type { RegenerableFieldNamespace } from "../../config/field-namespaces";
import {
  getEditorFieldGuidance,
  getEditorFieldSeoIndicator,
  type EditorFieldMetadata,
} from "../../lib/editor-field-guidance";
import { getEditorFieldValidationWarnings } from "../../lib/editor-field-validation";
import { getWindowJobsiteConfig } from "../../types/runtime-config";

interface RegenerableTextFieldProps {
  value: string;
  onChange: (value: string) => void;
  namespace: RegenerableFieldNamespace;
  label?: string;
  multiline?: boolean;
  placeholder?: string;
  fieldMeta?: EditorFieldMetadata;
}

export const RegenerableTextField: React.FC<RegenerableTextFieldProps> = ({
  value = "",
  onChange,
  namespace,
  label,
  multiline = false,
  placeholder,
  fieldMeta,
}) => {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const guidance = getEditorFieldGuidance(fieldMeta);
  const seoIndicator = getEditorFieldSeoIndicator(fieldMeta);
  const business = getWindowJobsiteConfig().business;
  const noteItems = [guidance.description, ...guidance.tips].filter((item): item is string =>
    Boolean(item),
  );
  const hasNotes = noteItems.length > 0;
  const warnings = getEditorFieldValidationWarnings({
    value,
    metadata: fieldMeta,
    business,
  });

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    setError(null);

    try {
      // Read site/page IDs from data attributes set by the editor
      const siteId = document.documentElement.dataset.editorSiteId;
      const pageId = document.documentElement.dataset.editorPageId;

      if (!siteId || !pageId) {
        throw new Error("Editor context not available. Please try refreshing the page.");
      }

      // Parse namespace to extract componentType and fieldName
      // Format: "pageType.componentType.fieldName" (e.g., "home.hero.headline")
      // or "componentType.fieldName" (e.g., "Hero.headline")
      const parts = namespace.split(".");
      let componentType: string;
      let fieldName: string;

      if (parts.length >= 3) {
        componentType = capitalizeFirst(parts[1]);
        fieldName = parts.slice(2).join(".");
      } else if (parts.length === 2) {
        componentType = capitalizeFirst(parts[0]);
        fieldName = parts[1];
      } else {
        throw new Error("Invalid namespace format");
      }

      const apiUrl = import.meta.env.VITE_API_URL || "";
      const url = `${apiUrl}/api/sites/${siteId}/pages/${pageId}/regenerate-field`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          componentType,
          fieldName,
          currentValue: value,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to regenerate content");
      }

      const data = await response.json();

      if (data.valid && data.content) {
        onChange(data.content);
      } else {
        throw new Error(data.error || "Generated content was invalid");
      }
    } catch (err: any) {
      console.error("Regeneration failed:", err);
      setError(err.message || "Failed to regenerate content");
    } finally {
      setIsRegenerating(false);
    }
  };

  const capitalizeFirst = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Inline styles using the host app's CSS variables for dark mode compatibility
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "8px 12px",
    fontSize: "14px",
    lineHeight: 1.5,
    fontFamily: "inherit",
    color: "var(--foreground, #0f172a)",
    backgroundColor: "var(--muted, #f3f4f6)",
    border: "1px solid var(--border, #e2e8f0)",
    borderRadius: "6px",
    boxSizing: "border-box",
    outline: "none",
  };

  return (
    <div>
      {label && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label
              style={{ fontSize: 14, fontWeight: 500, color: "var(--muted-foreground, #6b7280)" }}
            >
              {label}
            </label>
            {seoIndicator?.signal === "critical" ? (
              <span
                title={seoIndicator.description}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "2px 6px",
                  fontSize: 10,
                  fontWeight: 600,
                  lineHeight: 1,
                  color: "rgba(55, 65, 81, 0.85)",
                  backgroundColor: "rgba(243, 244, 246, 0.9)",
                  border: "1px solid rgba(203, 213, 225, 0.9)",
                  borderRadius: 999,
                }}
              >
                <Search size={10} />
                {seoIndicator.label}
              </span>
            ) : null}
            <button
              type="button"
              onClick={handleRegenerate}
              disabled={isRegenerating}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "2px 6px",
                fontSize: 11,
                fontWeight: 500,
                color: isRegenerating ? "#9CA3AF" : "#6366F1",
                backgroundColor: isRegenerating ? "#F3F4F6" : "#EEF2FF",
                border: `1px solid ${isRegenerating ? "#E5E7EB" : "#C7D2FE"}`,
                borderRadius: 4,
                cursor: isRegenerating ? "not-allowed" : "pointer",
                marginLeft: "auto",
              }}
            >
              {isRegenerating ? (
                <Loader2 size={12} style={{ animation: "spin 1s linear infinite" }} />
              ) : (
                <Sparkles size={12} />
              )}
              {isRegenerating ? "Generating..." : "Regenerate"}
            </button>
          </div>

          {(guidance.reassurance || hasNotes || warnings.length > 0) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "4px 10px",
                }}
              >
                {guidance.reassurance ? (
                  <p
                    style={{
                      margin: 0,
                      fontSize: 11,
                      lineHeight: 1.4,
                      color: "var(--muted-foreground, #6b7280)",
                    }}
                  >
                    {guidance.reassurance}
                  </p>
                ) : null}

                {hasNotes ? (
                  <button
                    type="button"
                    onClick={() => setIsNotesOpen((open) => !open)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: 0,
                      fontSize: 11,
                      fontWeight: 600,
                      lineHeight: 1.4,
                      color: "var(--muted-foreground, #6b7280)",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Notes
                    <ChevronDown
                      size={12}
                      style={{
                        transform: isNotesOpen ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 160ms ease",
                      }}
                    />
                  </button>
                ) : null}
              </div>

              {warnings.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {warnings.map((warning) => (
                    <div
                      key={`${label}-${warning.kind}-${warning.message}`}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 6,
                        fontSize: 11,
                        lineHeight: 1.4,
                        color: "rgb(180, 83, 9)",
                      }}
                    >
                      <AlertCircle size={12} style={{ marginTop: 2, flexShrink: 0 }} />
                      <span>{warning.message}</span>
                    </div>
                  ))}
                </div>
              ) : null}

              {hasNotes && isNotesOpen ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    paddingLeft: 12,
                    borderLeft: "1px solid rgba(148, 163, 184, 0.45)",
                  }}
                >
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: 0,
                      listStyle: "none",
                      fontSize: 11,
                      lineHeight: 1.5,
                      color: "var(--muted-foreground, #6b7280)",
                    }}
                  >
                    {noteItems.map((item) => (
                      <li
                        key={`${label}-${item}`}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 8,
                          marginTop: 2,
                        }}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            width: 6,
                            height: 6,
                            marginTop: 6,
                            borderRadius: "999px",
                            flexShrink: 0,
                            backgroundColor: "rgba(107, 114, 128, 0.75)",
                          }}
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}

      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          style={{ ...inputStyle, resize: "vertical" }}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={inputStyle}
        />
      )}

      {error && (
        <div
          style={{
            marginTop: 6,
            padding: "6px 10px",
            fontSize: 12,
            color: "var(--destructive, #dc2626)",
            backgroundColor: "rgba(220, 38, 38, 0.1)",
            border: "1px solid rgba(220, 38, 38, 0.2)",
            borderRadius: 4,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};
